import React, { useState, useEffect, useCallback } from 'react';
import { Mic, MicOff } from 'lucide-react';
import toast from 'react-hot-toast';

const DictationButton = ({ onTranscript, lang = 'fr-FR' }) => {
    const [isListening, setIsListening] = useState(false);
    const [recognition, setRecognition] = useState(null);

    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            const recog = new SpeechRecognition();
            recog.continuous = false; // We start/stop manually or it stops after silence
            recog.interimResults = false;
            recog.lang = lang;

            recog.onstart = () => {
                setIsListening(true);
            };

            recog.onresult = (event) => {
                let transcript = event.results[0][0].transcript;

                // Mappage manuel de la ponctuation courante en français
                const punctuationMap = {
                    'point': '.',
                    'virgule': ',',
                    'point d\'interrogation': '?',
                    'point d\'exclamation': '!',
                    'deux points': ':',
                    'point-virgule': ';',
                    'à la ligne': '\n',
                    'nouveau paragraphe': '\n\n',
                    'ouvrez les guillemets': '« ',
                    'fermez les guillemets': ' »'
                };

                // Conversion des mots-clés en symboles
                Object.keys(punctuationMap).forEach(key => {
                    const regex = new RegExp(`\\s?${key}\\s?`, 'gi');
                    transcript = transcript.replace(regex, (match) => {
                        // On garde un espace après si nécessaire
                        return punctuationMap[key] + (punctuationMap[key].length === 1 && !['\n', '«'].includes(punctuationMap[key]) ? ' ' : '');
                    });
                });

                // Nettoyage des espaces doubles et mise en majuscule du début
                transcript = transcript.trim();
                transcript = transcript.charAt(0).toUpperCase() + transcript.slice(1);

                onTranscript(transcript);
                setIsListening(false);
            };

            recog.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                if (event.error === 'not-allowed') {
                    toast.error('Accès au micro refusé. Veuillez vérifier les permissions.');
                } else if (event.error === 'network') {
                    toast.error('Erreur réseau (Dictée). Note: Brave bloque souvent ce service. Essayez Chrome ou autorisez les services Google dans Brave.', { duration: 6000 });
                } else if (event.error !== 'no-speech') {
                    toast.error(`Erreur dictée: ${event.error}`);
                }
                setIsListening(false);
            };

            recog.onend = () => {
                setIsListening(false);
            };

            setRecognition(recog);
        }
    }, [lang, onTranscript]);

    const toggleListening = useCallback(() => {
        if (!recognition) {
            toast.error("Votre navigateur ne supporte pas la dictée vocale.");
            return;
        }

        if (isListening) {
            recognition.stop();
        } else {
            try {
                recognition.start();
            } catch (err) {
                console.error('Recognition start error:', err);
            }
        }
    }, [recognition, isListening]);

    if (!recognition && !window.webkitSpeechRecognition && !window.SpeechRecognition) {
        return null;
    }

    return (
        <button
            type="button"
            className={`btn-dictation ${isListening ? 'listening' : ''}`}
            onClick={toggleListening}
            title={isListening ? 'Arrêter la dictée' : 'Démarrer la dictée vocale'}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                background: isListening ? 'var(--danger)' : 'var(--primary-50)',
                color: isListening ? 'white' : 'var(--primary-600)',
                boxShadow: isListening ? '0 0 10px var(--danger)' : 'none',
                position: 'relative'
            }}
        >
            {isListening ? <MicOff size={16} /> : <Mic size={16} />}
            {isListening && (
                <span className="pulse-ring" style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: '50%',
                    border: '2px solid var(--danger)',
                    animation: 'pulse 1.5s infinite'
                }} />
            )}
        </button>
    );
};

export default DictationButton;
